/**
 * Validation functions for Rig Calibration module
 */

import { VALIDATION_LIMITS } from './constants';
import { CalibrationValues, ValidationErrors } from './types';
import type { Language } from '@/i18n/LanguageContext';
import { formatFixedNumber } from '@/utils/numberFormat';

/**
 * Validate a single calibration value
 */
export function validateCalibrationValue(
  value: number,
  fieldName: string
): string | null {
  if (value < VALIDATION_LIMITS.MIN_VALUE || value > VALIDATION_LIMITS.MAX_VALUE) {
    return `${fieldName} must be between ${VALIDATION_LIMITS.MIN_VALUE} and ${VALIDATION_LIMITS.MAX_VALUE}`;
  }
  return null;
}

/**
 * Validate sum (Oil% + Water%)
 */
export function validateSum(
  oilValue: number,
  waterValue: number,
  sourceName: string,
  language: Language = 'en'
): string | null {
  const sum = oilValue + waterValue;
  if (sum > VALIDATION_LIMITS.MAX_SUM) {
    return `${sourceName}: Oil% + Water% (${formatFixedNumber(sum, language, 2)}%) must not exceed ${VALIDATION_LIMITS.MAX_SUM}%`;
  }
  return null;
}

/**
 * Validate all calibration values
 */
export function validateCalibrationValues(
  values: CalibrationValues
): ValidationErrors {
  const errors: ValidationErrors = {};

  // Validate all fields
  Object.entries(values).forEach(([key, value]) => {
    const error = validateCalibrationValue(value as number, key);
    if (error) {
      errors[key] = error;
    }
  });

  return errors;
}

/**
 * Validate a single source
 */
export function validateBucket(
  oilValue: number,
  waterValue: number,
  sourcePrefix: string,
  language: Language = 'en'
): ValidationErrors {
  const errors: ValidationErrors = {};

  const oilError = validateCalibrationValue(oilValue, `${sourcePrefix} Oil%`);
  if (oilError) {
    errors[`${sourcePrefix}_oil_calibration`] = oilError;
  }

  const waterError = validateCalibrationValue(waterValue, `${sourcePrefix} Water%`);
  if (waterError) {
    errors[`${sourcePrefix}_water_calibration`] = waterError;
  }

  const sumError = validateSum(oilValue, waterValue, sourcePrefix, language);
  if (sumError) {
    errors[`${sourcePrefix}_sum`] = sumError;
  }

  return errors;
}
